import { useMemo, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import { getPublicNeedsForm, submitPublicNeeds } from "@/services/publicQuote"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { serverDetail } from "@/services/public-fetch"
import { useToast } from "@/hooks/use-toast"
import { ClipboardList, CheckCircle2, AlertTriangle, FileText } from "lucide-react"

export default function PublicNeedsFormPage() {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const [submitted, setSubmitted] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["public-needs-form", token],
    queryFn: () => getPublicNeedsForm(token!),
    enabled: !!token,
    retry: false,
  })

  if (!token) return <Shell><Empty message="Token manquant" /></Shell>
  if (isLoading) return <Shell><div className="p-8 text-center text-muted-foreground">Chargement…</div></Shell>
  if (isError || !data) {
    const reason = pipe(serverDetail(error), O.getOrElse(() => "Lien indisponible."))
    return <Shell><Empty message={reason} /></Shell>
  }

  if (submitted) {
    return (
      <Shell tenantName={data.tenant_name}>
        <div className="mx-auto max-w-2xl p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <h1 className="mt-4 text-2xl font-semibold">Merci pour vos réponses</h1>
          <p className="mt-2 text-muted-foreground">
            Vos retours nous permettent d'ajuster le programme. Vous pouvez revenir compléter ce formulaire à tout moment, vos réponses précédentes restent enregistrées.
          </p>
          <Button asChild variant="outline" className="mt-6">
            <Link to={`/q/${token}`}>Revenir au devis</Link>
          </Button>
        </div>
      </Shell>
    )
  }

  return (
    <Shell tenantName={data.tenant_name}>
      <NeedsFormInner data={data} token={token} onSubmitted={() => {
        setSubmitted(true)
        toast({ title: "Réponses envoyées", description: "Merci, vos réponses ont bien été enregistrées." })
      }} />
    </Shell>
  )
}

function NeedsFormInner({
  data,
  token,
  onSubmitted,
}: {
  data: NonNullable<ReturnType<typeof getPublicNeedsForm> extends Promise<infer T> ? T : never>
  token: string
  onSubmitted: () => void
}) {
  const { toast } = useToast()
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [programId, setProgramId] = useState<string | undefined>(
    data.programs.length === 1 ? data.programs[0].id : undefined,
  )
  const [consent, setConsent] = useState(false)
  const [answers, setAnswers] = useState<Record<string, unknown>>({})

  const sortedQuestions = useMemo(
    () => [...data.questions].sort((a, b) => a.sort_order - b.sort_order),
    [data.questions],
  )

  const mutation = useMutation({
    mutationFn: () =>
      submitPublicNeeds(token, {
        email: email.trim(),
        name: name.trim() || undefined,
        program_id: programId,
        answers,
        consent,
      }),
    onSuccess: onSubmitted,
    onError: (e: Error) => toast({ variant: "destructive", title: "Erreur", description: e.message }),
  })

  const allRequiredAnswered = sortedQuestions.every((q) => {
    if (!q.is_required) return true
    const v = answers[q.id]
    if (v === undefined || v === null || v === "") return false
    if (Array.isArray(v) && v.length === 0) return false
    return true
  })

  const programRequired = data.programs.length > 1
  const canSubmit =
    !!email.trim() &&
    email.includes("@") &&
    consent &&
    allRequiredAnswered &&
    (!programRequired || !!programId) &&
    !mutation.isPending

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 md:p-8" data-test="public-needs-form">
      <header>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ClipboardList className="h-4 w-4" />
          Devis {data.quote_number}
        </div>
        <h1 className="mt-1 text-2xl font-semibold">Recueil des besoins — formation</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Quelques minutes pour partager vos attentes et calibrer la formation à vos besoins (Qualiopi indicateur 4). Vous pouvez revenir compléter vos réponses à tout moment.
        </p>
      </header>

      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Mentions RGPD</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs text-muted-foreground">
          {data.gdpr.controller_name && (
            <p><strong>Responsable de traitement :</strong> {data.gdpr.controller_name}</p>
          )}
          <p><strong>Finalité :</strong> {data.gdpr.purpose}</p>
          <p><strong>Conservation :</strong> {data.gdpr.retention}</p>
          <p><strong>Vos droits :</strong> {data.gdpr.rights}</p>
          {data.gdpr.dpo_email && <p><strong>Contact DPO :</strong> {data.gdpr.dpo_email}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Vos coordonnées</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="needs-email">Email *</Label>
            <Input
              id="needs-email"
              data-test="needs-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Obligatoire — permet à l'organisme de croiser vos réponses si vous revenez compléter le formulaire.
            </p>
          </div>
          <div>
            <Label htmlFor="needs-name">Nom (facultatif)</Label>
            <Input
              id="needs-name"
              data-test="needs-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Prénom Nom"
            />
          </div>
        </CardContent>
      </Card>

      {data.programs.length > 1 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Programme concerné *</CardTitle></CardHeader>
          <CardContent>
            <Select value={programId} onValueChange={setProgramId}>
              <SelectTrigger data-test="needs-program-select">
                <SelectValue placeholder="Sélectionnez le programme auquel votre réponse se rapporte" />
              </SelectTrigger>
              <SelectContent>
                {data.programs.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title} ({p.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Vos attentes</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {sortedQuestions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Le template de questions n'est pas encore configuré. Contactez l'organisme.
            </p>
          ) : (
            sortedQuestions.map((q) => (
              <QuestionField
                key={q.id}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
              />
            ))
          )}
        </CardContent>
      </Card>

      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-4">
          <div className="flex items-start gap-2">
            <Checkbox
              id="needs-consent"
              data-test="needs-consent"
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
            />
            <Label htmlFor="needs-consent" className="text-sm font-normal">
              J'accepte le traitement de mes réponses dans le cadre de la préparation de la formation, conformément aux mentions RGPD ci-dessus.
            </Label>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        <Button asChild variant="ghost">
          <Link to={`/q/${token}`}>Retour au devis</Link>
        </Button>
        <Button
          data-test="needs-submit"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
        >
          {mutation.isPending ? "Envoi…" : "Envoyer mes réponses"}
        </Button>
      </div>
    </div>
  )
}

type Question = {
  id: string
  question_text: string
  help_text: string | null
  question_type_code: string
  is_required: boolean
  sort_order: number
  options: { label: string; value: string; sort_order: number }[]
  min_value: number | null
  max_value: number | null
}

function QuestionField({
  question,
  value,
  onChange,
}: {
  question: Question
  value: unknown
  onChange: (v: unknown) => void
}) {
  const id = `q-${question.id}`
  const type = question.question_type_code

  return (
    <div data-test={`question-${question.id}`}>
      <Label htmlFor={id}>
        {question.question_text}
        {question.is_required && " *"}
      </Label>
      {question.help_text && <p className="mt-0.5 text-xs text-muted-foreground">{question.help_text}</p>}
      <div className="mt-2">
        {type === "text_long" && (
          <Textarea id={id} rows={4} value={(value as string) || ""} onChange={(e) => onChange(e.target.value)} />
        )}
        {type === "text_short" && (
          <Input id={id} value={(value as string) || ""} onChange={(e) => onChange(e.target.value)} />
        )}
        {type === "date" && (
          <Input id={id} type="date" value={(value as string) || ""} onChange={(e) => onChange(e.target.value)} />
        )}
        {type === "yes_no" && (
          <RadioGroup value={(value as string) || ""} onValueChange={onChange}>
            <div className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-yes`} value="yes" />
              <Label htmlFor={`${id}-yes`} className="font-normal">Oui</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-no`} value="no" />
              <Label htmlFor={`${id}-no`} className="font-normal">Non</Label>
            </div>
          </RadioGroup>
        )}
        {type === "single_choice" && (
          <RadioGroup value={(value as string) || ""} onValueChange={onChange}>
            {question.options.map((o) => (
              <div key={o.value} className="flex items-center gap-2">
                <RadioGroupItem id={`${id}-${o.value}`} value={o.value} />
                <Label htmlFor={`${id}-${o.value}`} className="font-normal">{o.label}</Label>
              </div>
            ))}
          </RadioGroup>
        )}
        {type === "multiple_choice" && (
          <div className="space-y-2">
            {question.options.map((o) => {
              const arr = Array.isArray(value) ? (value as string[]) : []
              const checked = arr.includes(o.value)
              return (
                <div key={o.value} className="flex items-center gap-2">
                  <Checkbox
                    id={`${id}-${o.value}`}
                    checked={checked}
                    onCheckedChange={(v) => {
                      if (v === true) onChange([...arr, o.value])
                      else onChange(arr.filter((x) => x !== o.value))
                    }}
                  />
                  <Label htmlFor={`${id}-${o.value}`} className="font-normal">{o.label}</Label>
                </div>
              )
            })}
          </div>
        )}
        {(type === "rating_5" || type === "rating_10" || type === "likert_5" || type === "nps") && (
          <RatingScale
            id={id}
            value={value as number | undefined}
            onChange={onChange}
            min={question.min_value ?? (type === "nps" ? 0 : 1)}
            max={question.max_value ?? (type === "rating_5" || type === "likert_5" ? 5 : 10)}
          />
        )}
      </div>
    </div>
  )
}

function RatingScale({
  id,
  value,
  onChange,
  min,
  max,
}: {
  id: string
  value: number | undefined
  onChange: (v: number) => void
  min: number
  max: number
}) {
  const values: number[] = []
  for (let i = min; i <= max; i++) values.push(i)
  return (
    <div id={id} className="flex flex-wrap gap-2">
      {values.map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => onChange(n)}
          className={`h-10 w-10 rounded-md border text-sm font-medium transition-colors ${
            value === n ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  )
}

function Shell({ tenantName, children }: { tenantName?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-2xl items-center gap-2 p-4 text-base font-medium">
          <FileText className="h-5 w-5 text-primary" />
          {tenantName || "Qualneo"}
        </div>
      </header>
      <main>{children}</main>
    </div>
  )
}

function Empty({ message }: { message: string }) {
  return (
    <div className="mx-auto mt-12 max-w-md p-8 text-center">
      <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
      <h2 className="mt-3 text-lg font-semibold">Formulaire indisponible</h2>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  )
}
